import { useState } from "react";
import {
  ReferenceInput,
  ReferenceArrayInput,
  AutocompleteInput,
  AutocompleteArrayInput,
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  useGetOne,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { RowForm } from "@react-admin/ra-editable-datagrid";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ENTITY_ACCESS_CHOICES = [
  { id: 1, name: "Preview" },
  { id: 2, name: "Preview + Read" },
  { id: 3, name: "Preview + Read + Update" },
];

// All available rule types. The dropdown is filtered down per source group's
// groupEntityType — a document group only shows DocumentsDynamicShare etc.,
// because a definition's source group has to match the entity type of what
// gets shared (SharedGroupEntities sourceGroupId is the source org's group of
// the matching entity type).
const ALL_RULE_TYPE_CHOICES = [
  { id: "InvestmentsDynamicShare", name: "Investments (by set)" },
  { id: "FirmsDynamicShare", name: "Firms (by investment set)" },
  { id: "PersonsDynamicShare", name: "Persons (by investment set)" },
  { id: "DocumentsDynamicShare", name: "Documents (by investment set)" },
  { id: "Everything", name: "Everything" },
];

// Map group entity type → the entity rule type that's valid for that group.
// Everything is always available regardless of entity type.
const ENTITY_TYPE_TO_RULE_TYPE: Record<string, string> = {
  investment: "InvestmentsDynamicShare",
  firm: "FirmsDynamicShare",
  person: "PersonsDynamicShare",
  document: "DocumentsDynamicShare",
};

function ruleTypeChoicesFor(groupEntityType: string | undefined) {
  const entityRuleType = groupEntityType
    ? ENTITY_TYPE_TO_RULE_TYPE[groupEntityType]
    : undefined;
  if (!entityRuleType) {
    // Group types without a dedicated rule type (portfolio, deal,
    // firmEngagement, applicationFlag) only support Everything for now.
    return ALL_RULE_TYPE_CHOICES.filter(c => c.id === "Everything");
  }
  return ALL_RULE_TYPE_CHOICES.filter(
    c => c.id === entityRuleType || c.id === "Everything"
  );
}

const INVESTMENT_SET_BASED_RULE_TYPES = new Set([
  "InvestmentsDynamicShare",
  "FirmsDynamicShare",
  "PersonsDynamicShare",
  "DocumentsDynamicShare",
]);

const SORT_BY_NAME = { field: "name", order: "ASC" } as const;

function getRuleTypeFromStructured(
  dynamicDef: any,
  defaultEntityRuleType: string | undefined
): string {
  if (dynamicDef?.investmentsDynamicShare) return "InvestmentsDynamicShare";
  if (dynamicDef?.firmsDynamicShare) return "FirmsDynamicShare";
  if (dynamicDef?.personsDynamicShare) return "PersonsDynamicShare";
  if (dynamicDef?.documentsDynamicShare) return "DocumentsDynamicShare";
  if (dynamicDef?.everything) return "Everything";
  // For new records, default to the rule type matching the source group's
  // entity type, falling back to Everything if none.
  return defaultEntityRuleType ?? "Everything";
}

function DocumentTypeSummary() {
  const ruleType = useWatch({ name: "ruleType" });
  const documentTypes = useWatch({ name: "documentTypes" });
  const setId = useWatch({ name: "setId" });

  if (ruleType !== "DocumentsDynamicShare") return null;
  if (!setId && !documentTypes?.length) return null;

  const hasBoth = setId && documentTypes?.length > 0;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        bgcolor: "info.50",
        border: "1px solid",
        borderColor: "info.200",
        borderRadius: 1,
      }}
    >
      <Typography variant="caption" color="info.main" fontWeight="medium">
        {hasBoth
          ? `This definition will share documents of the selected type(s) linked to investments in the selected set.`
          : setId
            ? `This definition will share ALL documents linked to investments in the selected set. Narrow it down by selecting document types.`
            : `Select an investment set to enable sharing.`}
      </Typography>
    </Box>
  );
}

function DynamicDefinitionInput() {
  const ruleType = useWatch({ name: "ruleType" });
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Dynamic Rule Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: "italic" }}>
            Configure how entities are dynamically determined for sharing.
          </Typography>

          {INVESTMENT_SET_BASED_RULE_TYPES.has(ruleType) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Investment Set Filter
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                {ruleType === "InvestmentsDynamicShare"
                  ? "Share investments matching the selected set."
                  : ruleType === "FirmsDynamicShare"
                    ? "Share firms attached to investments in the selected set."
                    : ruleType === "PersonsDynamicShare"
                      ? "Share persons attached (via firm employment) to investments in the selected set."
                      : "Share documents linked to investments in the selected set."}
              </Typography>

              <ReferenceInput source="setId" reference="set" alwaysOn>
                <AutocompleteInput
                  label="Investment Set"
                  optionText="name"
                  validate={required()}
                  helperText="Select an investment set"
                />
              </ReferenceInput>

              {ruleType === "DocumentsDynamicShare" && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Document Types (optional)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Restrict document sharing to specific types. Leave empty
                    to share documents of all types linked to investments in
                    the set.
                  </Typography>
                  <ReferenceArrayInput
                    source="documentTypes"
                    reference="documentTypeEnum"
                    alwaysOn
                  >
                    <AutocompleteArrayInput
                      label="Document Types"
                      optionText="name"
                      helperText="Documents of these types linked to investments in the set will be shared"
                    />
                  </ReferenceArrayInput>
                  <DocumentTypeSummary />
                </Box>
              )}
            </Box>
          )}

          {ruleType === "Everything" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Everything Rule
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Share ALL entities in the source group with the target group.
                No additional configuration needed.
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

interface GroupShareDefinitionFormProps {
  sourceGroupId: string | number;
}

export function GroupShareDefinitionForm({
  sourceGroupId,
}: GroupShareDefinitionFormProps) {
  // Get the source group to determine its entity type for filtering
  const { data: sourceGroup } = useGetOne("user", { id: sourceGroupId });

  const groupEntityType: string | undefined = sourceGroup?.groupEntityType;
  const ruleTypeChoices = ruleTypeChoicesFor(groupEntityType);
  const defaultEntityRuleType = groupEntityType
    ? ENTITY_TYPE_TO_RULE_TYPE[groupEntityType]
    : undefined;

  // Derive rule type from dynamicDefinitionStructured for editing; otherwise
  // default to whatever entity rule type matches the source group.
  const dynamicDef = useWatch({ name: "dynamicDefinitionStructured" });
  const initialRuleType = getRuleTypeFromStructured(
    dynamicDef,
    defaultEntityRuleType
  );

  return (
    <td colSpan={9}>
      <table style={{ width: "100%", tableLayout: "fixed" }}>
        <tbody>
          <RowForm>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  readOnly
                  source="sourceGroupId"
                  defaultValue={Number(sourceGroupId)}
                  label="Source Group ID"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ReferenceInput
                  source="targetGroupId"
                  reference="user"
                  filter={{
                    isGroup: true,
                    groupEntityType: sourceGroup?.groupEntityType,
                  }}
                  sort={SORT_BY_NAME}
                  alwaysOn
                >
                  <AutocompleteInput
                    label="Target Group"
                    optionText={record =>
                      `${record.commonName} (${record.userName}) - ${
                        record.organization?.name || "No Org"
                      } (ID: ${record.organizationId || "N/A"})`
                    }
                    validate={required()}
                  />
                </ReferenceInput>
              </Grid>

              <Grid item xs={12} md={6}>
                <SelectInput
                  source="ruleType"
                  label="Rule Type"
                  choices={ruleTypeChoices}
                  validate={required()}
                  alwaysOn
                  defaultValue={initialRuleType}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SelectInput
                  source="entityAccessId"
                  label="Entity Access Level"
                  choices={ENTITY_ACCESS_CHOICES}
                  validate={required()}
                  alwaysOn
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <BooleanInput
                  source="isActive"
                  label="Is Active"
                  defaultValue={true}
                />
              </Grid>

              <Grid item xs={12}>
                {/* Dynamic Definition Fields - Only shown for type 2 */}
                <DynamicDefinitionInput />
              </Grid>
            </Grid>
          </RowForm>
        </tbody>
      </table>
    </td>
  );
}
